import React, { useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Upload, Check, Bell, Volume2, Vibrate } from "lucide-react";
import Avatar, { PRESET_AVATARS } from "@/components/Avatar";
import { NOTIF_SOUNDS, playNotifSound, vibrationSupported } from "@/lib/notifySound";
import { subscribeToPush } from "@/lib/webPush";

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isAndroid = /Android/.test(navigator.userAgent);
const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone;

function NotificationSettings() {
  const { user, setUser } = useAuth();
  const prefs = user?.notif_prefs || { enabled: true, sound: "anggun", vibrate: true };
  const [saving, setSaving] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");

  const save = async (patch) => {
    const next = { enabled: prefs.enabled, sound: prefs.sound, vibrate: prefs.vibrate, ...patch };
    setSaving(true);
    try {
      await api.put("/notifications/prefs", next);
      setUser((u) => ({ ...u, notif_prefs: next }));
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const enableBrowserPermission = async () => {
    if (typeof Notification === "undefined") return;
    const perm = await Notification.requestPermission();
    setPermission(perm);
    if (perm === "granted") { try { await subscribeToPush(); } catch { /* best-effort */ } }
  };

  return (
    <div className="space-y-3 pt-1 border-t border-white/10 mt-1">
      <div className="flex items-center gap-2 pt-3">
        <Bell size={14} className="text-zinc-400" />
        <Label className="text-zinc-400 text-[11px] uppercase tracking-wide">Notifikasi</Label>
      </div>

      {permission !== "granted" && permission !== "unsupported" && (
        <div className="p-2.5 rounded-lg bg-blue-500/10 border border-blue-500/20">
          <p className="text-xs text-blue-200 mb-1.5">
            {permission === "denied"
              ? "Notifikasi diblokir browser. Aktifkan lewat setelan izin situs di browser kamu, lalu muat ulang halaman."
              : "Izinkan notifikasi supaya suara & pop-up bisa muncul walau aplikasi sedang di-minimize."}
          </p>
          {permission !== "denied" && (
            <button onClick={enableBrowserPermission} className="text-xs font-medium px-2.5 py-1 rounded-md bg-blue-600 hover:bg-blue-700 text-white">
              Izinkan Notifikasi
            </button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-sm">Notifikasi Aktif</span>
        <Switch checked={prefs.enabled !== false} onCheckedChange={(v) => save({ enabled: v })} disabled={saving} />
      </div>

      <div className={prefs.enabled === false ? "opacity-40 pointer-events-none space-y-3" : "space-y-3"}>
        <div className="flex items-center gap-2">
          <Volume2 size={13} className="text-zinc-500 shrink-0" />
          <Select value={prefs.sound || "anggun"} onValueChange={(v) => { save({ sound: v }); playNotifSound(v); }}>
            <SelectTrigger className="bg-[#18181B] border-white/10 h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#18181B] border-white/10">
              {NOTIF_SOUNDS.map((s) => <SelectItem key={s.id} value={s.id} className="text-xs">{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <button type="button" onClick={() => playNotifSound(prefs.sound)} className="text-[11px] text-blue-400 hover:text-blue-300 px-1.5 shrink-0">Tes</button>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-sm flex items-center gap-1.5">
            <Vibrate size={13} className="text-zinc-500" />Getar
            {!vibrationSupported && <span className="text-[10px] text-zinc-600">(tidak didukung di perangkat ini)</span>}
          </span>
          <Switch checked={prefs.vibrate !== false} onCheckedChange={(v) => save({ vibrate: v })} disabled={saving || !vibrationSupported} />
        </div>
        {isIOS && (
          <p className="text-[10px] text-zinc-600 -mt-2">
            iPhone (Safari/WebKit) tidak menyediakan akses getar untuk aplikasi web sama sekali — ini keterbatasan dari Apple sendiri, bukan sesuatu
            yang bisa diaktifkan dari sisi aplikasi ini, walaupun sudah ditambahkan ke Layar Utama. Fitur getar hanya berfungsi di Android (Chrome).
          </p>
        )}
      </div>

      <p className="text-[10px] text-zinc-600 leading-relaxed pt-1">
        {isIOS
          ? (isStandalone
            ? "Kalau notifikasi tidak muncul: buka Pengaturan iPhone → Notifikasi → cari aplikasi ini → aktifkan \"Izinkan Notifikasi\"."
            : "Untuk notifikasi paling stabil di iPhone, tambahkan aplikasi ini ke Layar Utama dulu (Share → Add to Home Screen), lalu buka dari ikon tersebut.")
          : isAndroid
            ? "Kalau notifikasi tidak muncul: buka Setelan HP → Aplikasi → cari aplikasi ini → Notifikasi → pastikan diizinkan."
            : "Kalau notifikasi tidak muncul, pastikan izin notifikasi untuk situs ini diaktifkan di setelan browser kamu."}
      </p>
    </div>
  );
}

export default function ProfileDialog({ open, onOpenChange }) {
  const { user, setUser } = useAuth();
  const fileRef = useRef();
  const [name, setName] = useState(user?.name || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/me", patch);
      setUser(data);
      toast.success("Profil diperbarui");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await save({ picture: data.path });
    } catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const saveName = async () => {
    if (!name.trim() || name.trim() === user?.name) return;
    await save({ name: name.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f12] border-white/10 max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading">Profil Saya</DialogTitle>
          <DialogDescription className="text-zinc-500">Ganti nama dan foto profil kamu.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3">
          <Avatar user={user} size={56} />
          <div className="flex-1 space-y-2">
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="profile-photo-input" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="h-9 bg-[#18181B] border-white/10 text-xs">
              {uploading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Upload size={14} className="mr-1.5" />}
              Upload Foto
            </Button>
          </div>
        </div>

        <div>
          <Label className="text-zinc-400 text-[11px] uppercase tracking-wide">Atau pilih avatar</Label>
          <div className="grid grid-cols-8 gap-2 mt-2">
            {PRESET_AVATARS.map((p) => (
              <button key={p.id} type="button" data-testid={`avatar-${p.id}`} onClick={() => save({ picture: p.id })}
                className="w-8 h-8 rounded-lg relative flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${p.from}, ${p.to})` }}>
                {user?.picture === p.id && <Check size={14} className="text-white drop-shadow" />}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-zinc-400 text-[11px] uppercase tracking-wide">Nama</Label>
          <div className="flex gap-2 mt-1.5">
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-[#18181B] border-white/10 h-9" />
            <Button type="button" onClick={saveName} disabled={saving || !name.trim() || name.trim() === user?.name} className="h-9 bg-blue-600 hover:bg-blue-700 shrink-0">
              {saving ? <Loader2 size={14} className="animate-spin" /> : "Simpan"}
            </Button>
          </div>
        </div>

        <NotificationSettings />
      </DialogContent>
    </Dialog>
  );
}
